import { arrayToObjectRange, objectToArrayRange, tree } from "../../RangeStyle";
const allProperties = [
  "bgColor",
  "fontWeight",
  "color",
  "fontStyle",
  "fontSize",
  "textAlign",
  "fontFamily",
  "verticalAlign",
  "dataType",
];

const utils = (context) => {
  const { dispatch, rootState, rootGetters, currentSheet, currentSheetId, currentSheetName, current, designId } =
    context;
  return {
    clearStyle(tree, range, onlyIntersectionItem = false) {
      const results = tree.search(range);

      if (results.length === 0 || results.every((item) => item.data.isTable)) {
        return;
      }
      let removedItems = [];

      results.forEach((item) => {
        if (item.data.isTable) return;

        const itemRange = objectToArrayRange(item);
        const mainRange = objectToArrayRange(range);
        const splitted = this.splitRanges(itemRange, mainRange);
        // in case we need list of items in intersection but don't want to remove styles
        if (!onlyIntersectionItem) {
          // Remove the original item from the tree
          tree.remove(item);
          // Insert non-intersecting parts back into the tree
          splitted.aOnly.forEach((range) => {
            tree.insert(arrayToObjectRange(range, item.data));
          });
        }
        splitted.intersection.forEach((range) => {
          removedItems.push(arrayToObjectRange(range, item.data));
        });
      });
      return removedItems;
    },
    splitRanges(A, B) {
      const [ax1, ay1, ax2, ay2] = A;
      const [bx1, by1, bx2, by2] = B;

      const ix1 = Math.max(ax1, bx1);
      const iy1 = Math.max(ay1, by1);
      const ix2 = Math.min(ax2, bx2);
      const iy2 = Math.min(ay2, by2);

      const result = {
        aOnly: [],
        bOnly: [],
        intersection: ix1 <= ix2 && iy1 <= iy2 ? [[ix1, iy1, ix2, iy2]] : [],
      };

      if (result.intersection.length > 0) {
        // if A includes B

        // Add parts of A that don't intersect
        if (ay1 < iy1) result.aOnly.push([ax1, ay1, ax2, iy1 - 1]); // Top part
        if (ay2 > iy2) result.aOnly.push([ax1, iy2 + 1, ax2, ay2]); // Bottom part
        if (ax1 < ix1) result.aOnly.push([ax1, iy1, ix1 - 1, iy2]); // Left part
        if (ax2 > ix2) result.aOnly.push([ix2 + 1, iy1, ax2, iy2]); // Right part

        // Add parts of B that don't intersect
        if (by1 < iy1) result.bOnly.push([bx1, by1, bx2, iy1 - 1]); // Top part
        if (by2 > iy2) result.bOnly.push([bx1, iy2 + 1, bx2, by2]); // Bottom part
        if (bx1 < ix1) result.bOnly.push([bx1, iy1, ix1 - 1, iy2]); // Left part
        if (bx2 > ix2) result.bOnly.push([ix2 + 1, iy1, bx2, iy2]); // Right part
      } else {
        return {
          aOnly: [...A],
          bOnly: [...B],
          intersection: [],
        };
      }

      return result;
    },
    mergeRanges(ranges) {
      let changesDone = false;
      let newRanges = [];

      ranges.forEach((range) => {
        let merged = false;
        for (let i = 0; i < newRanges.length; i++) {
          if (this.isDataSimilar(newRanges[i].data, range.data)) {
            if (this.isHAdjacent(newRanges[i], range)) {
              newRanges[i] = this.combine(newRanges[i], range);
              changesDone = true;
              merged = true;
              break;
            } else if (this.isVAdjacent(newRanges[i], range)) {
              newRanges[i] = this.combine(newRanges[i], range);
              changesDone = true;
              merged = true;
              break;
            }
          }
        }
        if (!merged) newRanges.push({ ...range });
      });
      if (changesDone) return this.mergeRanges(newRanges);
      return newRanges;
    },
    isDataSimilar(data1, data2) {
      return allProperties.every((item) => data1[item] === data2[item]);
    },
    // are two ranges horizontally adjacent or not
    isHAdjacent(range1, range2) {
      [range1, range2] = range1.minX < range2.minX ? [range1, range2] : [range2, range1];
      return range1.minY === range2.minY && range1.maxY === range2.maxY && range1.maxX === range2.minX - 1;
    },
    isVAdjacent(range1, range2) {
      [range1, range2] = range1.minY < range2.minY ? [range1, range2] : [range2, range1];
      return range1.minX === range2.minX && range1.maxX === range2.maxX && range1.maxY === range2.minY - 1;
    },
    combine(range1, range2) {
      return {
        minX: Math.min(range1.minX, range2.minX),
        minY: Math.min(range1.minY, range2.minY),
        maxX: Math.max(range1.maxX, range2.maxX),
        maxY: Math.max(range1.maxY, range2.maxY),
        data: { ...range1.data },
      };
    },
    findAndMergeRangesWithContainment(originalRange, tree, padding = 1) {
      // Step 1: Define the padded search area
      const searchRange = {
        minX: originalRange.minX - padding,
        maxX: originalRange.maxX + padding,
        minY: originalRange.minY - padding,
        maxY: originalRange.maxY + padding,
      };

      // Step 2: Find all ranges within the padded area
      const foundRanges = tree.search(searchRange);

      // If no ranges found, return early
      if (foundRanges.length === 0) return { foundRanges, mergedRanges: [] };

      // Step 3: Merge adjacent ranges
      const mergedRanges = this.mergeRanges(foundRanges);
      // Return all initially found ranges and final merged ranges
      return {
        foundRanges, // Ranges initially found before merging
        mergedRanges, // Merged result after processing
      };
    },
    copyStyleForSeries(initialRange, newRange, intersectionList) {
      // Calculate direction and axis
      const isVertical = newRange.maxX === initialRange.maxX && newRange.minX === initialRange.minX;
      const axis = isVertical ? "Y" : "X";
      const direction = newRange["max" + axis] > initialRange["max" + axis] ? "positive" : "negative";

      // Calculate delta (pattern length)
      const delta = {
        X: initialRange.maxX - initialRange.minX + 1,
        Y: initialRange.maxY - initialRange.minY + 1,
      };
      if (!intersectionList || intersectionList.length <= 0) return;

      let result = [...intersectionList];
      // Calculate pattern repetitions based on direction
      const patternRepetitions =
        direction === "positive"
          ? Math.abs(Math.floor((newRange["max" + axis] - initialRange["max" + axis]) / delta[axis]))
          : Math.abs(Math.floor((initialRange["min" + axis] - newRange["min" + axis]) / delta[axis]));

      // Copy pattern for each repetition
      for (let rep = 1; rep <= patternRepetitions + 1; rep++) {
        intersectionList.forEach((range) => {
          let newItem = { ...range };
          const offset = direction === "positive" ? delta[axis] * rep : -delta[axis] * rep;

          // Update dynamic properties (X or Y based on direction)
          newItem["min" + axis] += offset;
          newItem["max" + axis] += offset;

          // Handle boundary cases by truncating items instead of skipping
          if (direction === "positive") {
            if (newItem["min" + axis] <= newRange["max" + axis]) {
              // Truncate if item exceeds boundary
              if (newItem["max" + axis] > newRange["max" + axis]) {
                newItem["max" + axis] = newRange["max" + axis];
              }
              result.push(newItem);
            }
          } else {
            if (newItem["max" + axis] >= newRange["min" + axis]) {
              // Truncate if item exceeds boundary
              if (newItem["min" + axis] < newRange["min" + axis]) {
                newItem["min" + axis] = newRange["min" + axis];
              }
              result.push(newItem);
            }
          }
        });
      }

      return result;
    },
    formatPainter(initialRange, newRange, intersectionList) {
      if (!intersectionList || intersectionList.length <= 0) return [];

      // Calculate deltas (pattern dimensions)
      const delta = {
        X: initialRange.maxX - initialRange.minX + 1,
        Y: initialRange.maxY - initialRange.minY + 1,
      };

      // Calculate repetitions needed for both axes starting from target range minimum
      const repetitions = {
        X: {
          start: 0,
          end: Math.ceil((newRange.maxX - newRange.minX) / delta.X),
        },
        Y: {
          start: 0,
          end: Math.ceil((newRange.maxY - newRange.minY) / delta.Y),
        },
      };

      let result = [];

      // Iterate through all combinations of X and Y repetitions
      for (let repY = repetitions.Y.start; repY <= repetitions.Y.end; repY++) {
        for (let repX = repetitions.X.start; repX <= repetitions.X.end; repX++) {
          intersectionList.forEach((range) => {
            let newItem = { ...range };

            // Calculate relative position from source to target
            const baseOffsetX = newRange.minX - initialRange.minX;
            const baseOffsetY = newRange.minY - initialRange.minY;

            // Apply offsets including both base offset and pattern repetition
            newItem.minX += baseOffsetX + delta.X * repX;
            newItem.maxX += baseOffsetX + delta.X * repX;
            newItem.minY += baseOffsetY + delta.Y * repY;
            newItem.maxY += baseOffsetY + delta.Y * repY;

            // Check if the new item intersects with the target range
            if (
              newItem.minX <= newRange.maxX &&
              newItem.maxX >= newRange.minX &&
              newItem.minY <= newRange.maxY &&
              newItem.maxY >= newRange.minY
            ) {
              // Truncate to target range boundaries if necessary
              newItem.minX = Math.max(newItem.minX, newRange.minX);
              newItem.maxX = Math.min(newItem.maxX, newRange.maxX);
              newItem.minY = Math.max(newItem.minY, newRange.minY);
              newItem.maxY = Math.min(newItem.maxY, newRange.maxY);

              result.push(newItem);
            }
          });
        }
      }

      return result;
    },
  };
};

const main = (context) => {
  const { dispatch, rootState, rootGetters, current } = context;
  return {
    add(data, range) {
      range = range ?? rootGetters["cells/mainRange"].range;
      const previousStyles = [...tree.getIntersectionList(range, (item) => item.data.isTable)];
      tree.insertWithSplitting(range, data, (item) => item.data.isTable);
      return () => {
        tree.clearRange(range, (item) => item.data.isTable);
        previousStyles.forEach((style) => {
          tree.insertWithSplitting(objectToArrayRange(style), style.data, (item) => item.data.isTable);
        });
      };
    },
    set(data, range) {
      range = range ?? rootGetters["cells/mainRange"].range;
      const previousStyles = [...tree.getIntersectionList(range, (item) => item.data.isTable)];
      tree.insertWithSplitting(range, data, (item) => item.data.isTable);
      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
      return () => {
        tree.clearRange(range, (item) => item.data.isTable);
        previousStyles.forEach((style) => {
          tree.insertWithSplitting(objectToArrayRange(style), style.data, (item) => item.data.isTable);
        });
        dispatch("rangeStyle/persist", null, { root: true });
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
    clear(range) {
      range = range ?? rootGetters["cells/mainRange"].range;
      const returnFunc = this.clearRange(range);

      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });

      return () => {
        returnFunc();
        dispatch("rangeStyle/persist", null, { root: true });
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
    clearRange(range) {
      const previousStyles = [...tree.search(arrayToObjectRange(range)).filter((item) => !item.data.isTable)];
      tree.clearRange(range, (item) => item.data.isTable);

      return () => {
        tree.clearRange(range, (item) => item.data.isTable);
        previousStyles.forEach((style) => {
          tree.insertWithSplitting(objectToArrayRange(style), style.data, (item) => item.data.isTable);
        });
      };
    },
    copyStyleFromSourceToTarget(sourceRange, targetRange) {
      const previousStyles = [...tree.search(arrayToObjectRange(sourceRange)).filter((item) => !item.data.isTable)];
      const returnFunc = this.clearRange(targetRange);
      previousStyles.forEach((style) => {
        tree.insertWithSplitting(objectToArrayRange(style), style.data, (item) => item.data.isTable);
      });
      this.updateFillStyle(sourceRange, targetRange, true);
      return returnFunc;
    },
    addTableStyle(data, range) {
      tree.insert({
        ...arrayToObjectRange(range),
        data: { ...data },
      });
      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
      return () => {
        tree.clearRange(range);
        dispatch("rangeStyle/persist", null, { root: true });
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
    removeTableStyle(range) {
      const [minX, minY, maxX, maxY] = range;
      range = { minX, minY, maxX, maxY };
      const results = tree.search(range);
      const prevStyles = [];
      results.forEach((item) => {
        if (item.data.isTable) {
          prevStyles.push(item);
          tree.remove(item);
        }
      });
      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
      return () => {
        prevStyles.forEach((style) => {
          tree.insertWithSplitting(objectToArrayRange(style), style.data);
        });
        dispatch("rangeStyle/persist", null, { root: true });
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
    updateTableRangeStyle(oldRange, newRange, data) {
      const [minX, minY, maxX, maxY] = oldRange;
      const results = tree.search({ minX, minY, maxX, maxY });
      const oldItem = results.find((result) => result.data.isTable);
      tree.remove(oldItem);
      const newItem = {
        minX: newRange[0],
        minY: newRange[1],
        maxX: newRange[2],
        maxY: newRange[3],
        data: { ...data },
      };
      tree.insert(newItem);
      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });

      return () => {
        tree.remove(newItem);
        tree.insert(oldItem);
        dispatch("rangeStyle/persist", null, { root: true });
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
    moveRangeStyle(index, delta, axis, isPositive, steps = 1) {
      const dynamicMinProp = axis === "Y" ? "minY" : "minX";
      const dynamicMaxProp = axis === "Y" ? "maxY" : "maxX";
      const staticMinProp = axis === "Y" ? "minX" : "minY";
      const staticMaxProp = axis === "Y" ? "maxX" : "maxY";
      const adjustedDelta = isPositive ? steps : -steps;
      const targetStart = (isPositive ? delta - 1 : 0) + index + adjustedDelta;
      const minProp = `min${axis}`;
      const maxProp = `max${axis}`;
      const maxLength =
        axis === "Y" ? rootState.cells.current?.design.columns.length : rootState.cells.current?.design.rows.length;
      // Construct source and target ranges
      const sourceRange = {
        [staticMinProp]: 0,
        [staticMaxProp]: maxLength,
        [dynamicMinProp]: index,
        [dynamicMaxProp]: index + delta - 1,
      };

      const targetRange = {
        [staticMinProp]: 0,
        [staticMaxProp]: maxLength,
        [dynamicMinProp]: targetStart,
        [dynamicMaxProp]: targetStart,
      };

      // Calculate movement offsets
      const forwardOffset = adjustedDelta;
      const backwardOffset = -adjustedDelta * delta;

      const targetStylesToMove = utils(context).clearStyle(tree, targetRange);

      const sourceStylesToMove = utils(context).clearStyle(tree, sourceRange);

      sourceStylesToMove?.forEach((item) => {
        item[minProp] += forwardOffset;
        item[maxProp] += forwardOffset;

        tree.insert({
          ...item,
        });
      });

      targetStylesToMove?.forEach((item) => {
        item[minProp] += backwardOffset;
        item[maxProp] += backwardOffset;
        tree.insert({
          ...item,
        });
      });

      const updateRangesInTree = (range, tree) => {
        const result = utils(context).findAndMergeRangesWithContainment(range, tree);

        // Remove all found ranges from tree
        result.foundRanges.forEach((range) => {
          tree.remove(range);
        });

        // Insert merged ranges back into tree
        result.mergedRanges.forEach((range) => {
          tree.insert(range);
        });

        return result;
      };
      updateRangesInTree(utils(context).combine(sourceRange, targetRange), tree);
      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
      return () => {
        this.moveRangeStyle(index + (isPositive ? steps : -steps), delta, axis, !isPositive, steps);
      };
    },
    updateFillStyle(initialRange, newRange, isFormatPainter = true) {
      const previousStyles = [...tree.getIntersectionList(newRange, (item) => item.data.isTable)];
      const isSingleCell = newRange && newRange[0] === newRange[2] && newRange[1] === newRange[3];

      if (isSingleCell) {
        const [startX, startY, endX, endY] = initialRange;
        newRange[2] = newRange[0] + (endX - startX);
        newRange[3] = newRange[1] + (endY - startY);
      }
      newRange = arrayToObjectRange(newRange);
      initialRange = arrayToObjectRange(initialRange);

      const intersectionList = utils(context).clearStyle(tree, initialRange, isFormatPainter);
      utils(context).clearStyle(tree, newRange);
      const newStyleList = isFormatPainter
        ? utils(context).formatPainter(initialRange, newRange, intersectionList)
        : utils(context).copyStyleForSeries(initialRange, newRange, intersectionList);

      newStyleList?.forEach((style) => {
        tree.insert(style);
      });

      const mergeResult = utils(context).findAndMergeRangesWithContainment(newRange, tree);

      mergeResult.foundRanges.forEach((range) => {
        tree.remove(range);
      });
      mergeResult.mergedRanges.forEach((range) => {
        tree.insert(range);
      });
      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
      dispatch("cells/formatPainterData", { isFormatPainterActive: false }, { root: true });
      return () => {
        tree.clearRange(objectToArrayRange(newRange), (item) => item.data.isTable);
        previousStyles.forEach((style) => {
          tree.insertWithSplitting(objectToArrayRange(style), style.data, (item) => item.data.isTable);
        });
        dispatch("rangeStyle/persist", null, { root: true });
        dispatch("cells/toggleRedrawCanvas", null, { root: true });
      };
    },
    updateItems(index, delta, isInsert, axis) {
      const range =
        axis === "Y"
          ? [0, index, current.design.columns.length, index + delta]
          : [index, 0, index + delta, current.design.rows.length];

      const previousStyles = [...tree.getIntersectionList(range, (item) => item.data.isTable)];

      switch (axis) {
        case "Y":
          if (isInsert) tree.insertRow(index, delta, (item) => item.data.isTable);
          else tree.deleteRow(index, delta, (item) => item.data.isTable);
          break;
        default:
          if (isInsert) tree.insertColumn(index, delta, (item) => item.data.isTable);
          else tree.deleteColumn(index, delta, (item) => item.data.isTable);
          break;
      }
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
      dispatch("rangeStyle/persist", null, { root: true });
      return () => {
        this.updateItems(index, delta, !isInsert, axis);
        previousStyles.forEach((style) => {
          tree.insertWithSplitting(objectToArrayRange(style), style.data, (item) => item.data.isTable);
        });
        dispatch("rangeStyle/persist", null, { root: true });
      };
    },
  };
};
export default (context) => {
  return {
    main: main(context),
    utils: utils(context),
  };
};
